import Permissions from '../../Utils/Permissions';
import KnowledgeBases from './Areas/KnowledgeBases';
import AgentBuilder from './Areas/AgentBuilder';
import Activity from './Areas/Activity';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE EXTENSION POINT. This list IS the AI Admin area.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * To add an AI function or setting screen — a prompt editor, model settings,
 * an extraction playground — add ONE entry here and write its component.
 * Nothing else needs editing: the section selector, the deep-linkable route
 * (/ai-admin/<key>) and the landing area all read this list, so there is no
 * route to register, no tab to wire up and no nav entry to add. That is the
 * whole reason this directory is shaped as a shell plus Areas/ rather than
 * one page.
 *
 * Entry shape:
 *   key        — URL segment under /ai-admin, and the area's stable identity.
 *                Changing it changes a bookmarkable URL, so treat it as one.
 *   label      — what the section selector and the landing kicker call it
 *   icon       — Font Awesome class for the section selector
 *   permission — the module gate for THIS area (see PERMISSIONS below)
 *   Component  — the area's screen. It owns its own heading and actions; the
 *                shell owns only the area selector above it.
 *
 * PERMISSIONS: today the whole area is gated once, at the route and the nav
 * entry, on Permissions.MANAGE_AI — so every entry below repeats that same
 * permission and the gate is effectively area-wide. An area that needs
 * NARROWER access later puts its own permission string here instead, and at
 * that point the section selector must start filtering this list by
 * `permission` (UserService.canAccessModule) instead of showing all of it —
 * otherwise someone will be offered a section that 403s. That filtering is
 * deliberately NOT built while every entry carries the same permission,
 * because a filter that can only ever show everything or nothing is a trap of
 * its own; the field is here so the data is ready for it. Do not assume
 * MANAGE_AI is the only permission that can appear in this list.
 */
export const AI_AREAS = [
  {
    key: 'knowledge-bases',
    label: 'Knowledge Bases',
    icon: 'fa-book',
    permission: Permissions.MANAGE_AI,
    Component: KnowledgeBases,
  },
  {
    /**
     * THE KEY AND THE LABEL DISAGREE ON PURPOSE.
     *
     * The area began as an instruction editor and its URL is bookmarked as
     * `agent-instructions`. It now also carries the tool authorities, the
     * scope mode and the dry run — the whole of what configures an agent —
     * because all four are one draft row on the server and one publish. So
     * the LABEL caught up with the screen's job and the KEY did not: per the
     * note above, the key is a bookmarkable URL and is treated as one.
     */
    key: 'agent-instructions',
    label: 'Agent Builder',
    icon: 'fa-screwdriver-wrench',
    permission: Permissions.MANAGE_AI,
    Component: AgentBuilder,
  },
  {
    key: 'activity',
    label: 'Activity',
    icon: 'fa-wave-square',
    permission: Permissions.MANAGE_AI,
    Component: Activity,
  },
];

/** Where /ai-admin lands: the first registered area. */
export const DEFAULT_AREA_KEY = AI_AREAS[0].key;

/**
 * The area a URL segment means. An unknown or missing segment resolves to
 * the default rather than a dead end — /ai-admin itself has no segment, and
 * a stale bookmark from a removed area should still land somewhere useful.
 */
export function resolveArea(key) {
  return AI_AREAS.find((area) => area.key === key) || AI_AREAS[0];
}
